import { ApiService } from '@b2b/common/services/api/types';
import { TODO } from '@b2b/types/core';
import angular, { IHttpResponse, IHttpService, IPromise } from 'angular';
import { BehaviorSubject } from 'rxjs';
import * as utf8 from 'utf8';
import { PermissionsService } from '../permissions';
import { Impersonation, UserSession } from './types';

export type AuthenticationStatus = 'unknown' | 'authenticated' | 'unauthenticated';

export interface AuthenticationState {
  state: AuthenticationStatus;
  session: UserSession | null;
  impersonation: Impersonation | null;
}

function createInitialState(values: Partial<AuthenticationState> = {}): AuthenticationState {
  return {
    state: 'unknown',
    session: null,
    impersonation: null,
    ...values,
  };
}

export type AuthenticationEvents =
  | { type: 'session.initialized'; params: { session: UserSession | null; impersonation: Impersonation | null } }
  | { type: 'login.success'; params: { session: UserSession } }
  | { type: 'logout' }
  | { type: 'csr.impersonation.changed'; params: { session: UserSession; impersonation: Impersonation | null } }
  | { type: 'sso.callback.success'; params: { session: UserSession } };

export class AuthenticationService {
  static $inject = ['$http', 'ApiService', 'AuthService', 'PermissionsService', 'TenantService'];
  private stateSubject = new BehaviorSubject<AuthenticationState>(createInitialState());
  state$ = this.stateSubject.asObservable();

  constructor(
    private $http: IHttpService,
    private apiService: ApiService,
    private authService: TODO,
    private permissionsService: PermissionsService,
    private tenantService: TODO,
  ) {
    this.initializeSession();
  }

  getSnapshot() {
    return this.stateSubject.value;
  }

  send(event: AuthenticationEvents) {
    switch (event.type) {
      case 'session.initialized': {
        this.stateSubject.next({
          ...this.stateSubject.value,
          state: event.params.session ? 'authenticated' : 'unauthenticated',
          session: event.params.session,
          impersonation: event.params.impersonation,
        });
        break;
      }

      case 'login.success': {
        this.stateSubject.next({
          ...this.stateSubject.value,
          state: 'authenticated',
          session: event.params.session,
        });
        break;
      }

      case 'logout': {
        this.stateSubject.next({
          state: 'unauthenticated',
          session: null,
          impersonation: null,
        });
        break;
      }

      case 'csr.impersonation.changed': {
        this.stateSubject.next({
          ...this.stateSubject.value,
          session: event.params.session,
          impersonation: event.params.impersonation,
        });
        break;
      }

      case 'sso.callback.success': {
        this.stateSubject.next({
          ...this.stateSubject.value,
          state: 'authenticated',
          session: event.params.session,
        });
        break;
      }

      default: {
        break;
      }
    }
  }

  destroy() {
    this.send({ type: 'logout' });
    this.authService.destroy();
  }

  deputySignIn(userId: number) {
    return this.apiService.customerService.deputySignIn(userId).then((response) => {
      this.send({
        type: 'csr.impersonation.changed',
        params: {
          session: response.data.payload.user,
          impersonation: response.data.payload.impersonation,
        },
      });

      return response;
    });
  }

  login(props: { email: string; password: string; header?: { name: string; value: string } }) {
    return this.apiService.user.login(props);
  }

  logout(): IPromise<IHttpResponse<{ success: true }>> {
    return this.authService.logout().then((response) => {
      this.destroy();
      return response;
    });
  }

  getUserFullName(): string {
    const csr = this.getCSRUserFromCookie();
    const csrLabel = this.mapUserSessionToLabel(csr);
    const userLabel = this.mapUserSessionToLabel(this.getUserFromCookie());

    if (csr == null) {
      return utf8.decode(userLabel);
    }

    if (csrLabel === userLabel) {
      return utf8.decode(`${csrLabel} (CSR)`);
    }

    return utf8.decode(`${csrLabel} (as ${userLabel})`);
  }

  getUserFromCookie(): UserSession | null {
    const user: UserSession | null = this.authService.getCookie('user');

    if (user == null) {
      return null;
    }

    return { ...user, authenticationType: user.authenticationType ?? 'LEGACY' };
  }

  getCSRUserFromCookie(): UserSession | null {
    const csr: UserSession | null = this.authService.getCookie('csr');

    if (csr == null) {
      return null;
    }

    return csr;
  }

  isCSR() {
    if (this.stateSubject.value.impersonation != null) {
      return true;
    }

    return this.permissionsService.check('CSR');
  }

  isCSRImpersonating() {
    return this.getImpersonation() != null;
  }

  getImpersonation(): Impersonation | null {
    if (this.stateSubject.value.impersonation != null) {
      return this.stateSubject.value.impersonation;
    }

    return this.getImpersonationFromCookies();
  }

  isSSO() {
    return this.getUserFromCookie()?.authenticationType === 'SSO';
  }

  isPasswordRotationEnabled() {
    return !!this.getUserFromCookie()?.passwordStatus;
  }

  ssoCallback(params: { RelayState: string }) {
    return this.$http.get<{ session: UserSession }>('/v2/api/sso/callback', { params });
  }

  getRedirectState(): string | false {
    const user = this.getUserFromCookie();

    if (user == null) {
      return false;
    }

    if (user.confirmed && user.onBoarded) {
      return 'application.programs.list';
    }

    return 'onboard.default';
  }

  completeLogin(response: { data: { session: TODO } }) {
    const userId = response.data.session.id;
    const session = response.data.session;

    return this.apiService.system.csrf().then((csrfResponse) => {
      this.authService.setCSRF(csrfResponse.data.payload);

      if (this.$http.defaults.headers && this.$http.defaults.xsrfHeaderName) {
        this.$http.defaults.headers.common[this.$http.defaults.xsrfHeaderName] = this.authService.getCSRF('token');
        this.$http.defaults.headers.post[this.$http.defaults.xsrfHeaderName] = this.authService.getCSRF('token');
        this.$http.defaults.headers.put[this.$http.defaults.xsrfHeaderName] = this.authService.getCSRF('token');
      }

      return this.completePermissions(userId)
        .then(() => {
          this.send({ type: 'login.success', params: { session } });
        })
        .catch((response) => {
          if (response.status === 401 || response.status === 440) {
            this.destroy();
            // $scope.$emit('LoginController::error::unauthorized');
          } else {
            // $scope.$emit('LoginController::error');
          }

          throw response;
        });
    });
  }

  private getImpersonationFromCookies(): Impersonation | null {
    const csr = this.getCSRUserFromCookie();
    const user = this.getUserFromCookie();

    if (csr != null && user != null && csr.id !== user.id) {
      return { csr };
    }

    return null;
  }

  private initializeSession() {
    const session = this.getUserFromCookie();
    const impersonation = this.getImpersonationFromCookies();
    this.send({ type: 'session.initialized', params: { session, impersonation } });
  }

  private completePermissions(userId: number) {
    return this.apiService.user.permissions.list(userId).then((response) => {
      const permissions = response.data.payload;

      return this.apiService.tenant.config.list().then((response) => {
        if (response.data.payload.list.length > 0) {
          this.tenantService.setModel(response.data.payload.list[0]);
        }

        if (this.tenantService.isRecurringPaymentEnabled() && permissions.indexOf('SUBSCRIPTION_VIEW') >= 0) {
          permissions.push('RECURRING_PAYMENT_ENABLED');
        }

        this.authService.setPermissions(permissions);
      });
    });
  }

  private mapUserSessionToLabel(
    props: {
      firstName: string | null;
      lastName: string | null;
      email: string | null;
      userName: string | null;
    } | null,
  ) {
    if (props == null) {
      return '';
    }

    if (props.firstName != null && props.firstName !== '' && props.lastName != null && props.lastName !== '') {
      return `${props.firstName} ${props.lastName}`;
    }

    if (props.email != null && props.email !== '') {
      return props.email;
    }

    if (props.userName != null && props.userName !== '') {
      return props.userName;
    }

    return '';
  }
}

export const AuthenticationServiceModule = angular
  .module('acme.common.service.authentication', [
    'acme.common.service.api',
    'acme.common.service.auth',
    'acme.common.service.tenant',
  ])
  .service('AuthenticationService', AuthenticationService).name;
